import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	Validators,
} from '@angular/forms';
import { Logger } from '@core/logger';
import { clientIsPerson } from '@core/util/client';
import {
	ClientSigningDetailsViewModelComplete,
	EnvelopeStatusEnum,
	ESigningRecipient,
	ParticipantTypeEnum,
	SendEnvelopeRequest,
	SigningElementPackageViewModelComplete,
	UpdateEnvelopeRequest,
} from '@models/e-signing';
import { dispatch, select } from '@ngxs/store';
import { ClientsState } from '@state/clients';
import { MatterClients } from '@state/global/matter_clients';
import { ModalService } from '@ui/modal/modal.service';
import { ComponentConfig, UI_TYPES } from 'src/app/ui/modal/modal.types';
import { ESigningEditRecipientModalViewComponent } from 'src/app/private/sale/documents/envelopes/modals/e-signing-edit-recipient-modal-view/e-signing-edit-recipient-modal-view.component';
import { ESigningSendEnvelopeModalComponent } from 'src/app/private/sale/documents/envelopes/modals/e-signing-send-envelope-modal/e-signing-send-envelope-modal.component';
import { SignItApiService } from '../../api-repos/signit/signit.api.service';
import {
	catchError,
	firstValueFrom,
	map,
	Observable,
	of,
	startWith,
	switchMap,
	forkJoin,
	tap,
	throwError,
} from 'rxjs';
import { NavigationService } from '@services/nav/nav.service';
import { Router } from '@angular/router';
import { ESigningBlockChange } from 'src/app/private/sale/documents/envelopes/envelope-draft/e-signing-canvas/e-signing-canvas.component';
import { EnvelopeConfigRepo } from '@repos/envelope-config/envelope-config.repo';
import { STATUSES } from '@models/statuses';
import { MatterPackagesSelectors } from '@state/matter/matter-packages/matter-packages.selectors';
import { AuthState } from '@state/auth';
import { ToastService } from 'src/app/ui/toast/toast.service';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { HttpErrorResponse } from '@angular/common/http';
import { downloadBase64File, downloadEnvelopePdf } from '@core/util/file';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { getInitials } from '@core/util/string';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { where } from 'firebase/firestore';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { PreparePackageForSigningOptions } from '../../api-repos/signit/preparePackageForSigningOptions';
import { ViewEnvelopeDrawerComponent } from 'src/app/private/sale/documents/envelopes/drawers/view-envelope-drawer/view-envelope-drawer.component';
import { FileDownloadActions } from '@state/file-download/fileDownload.index';
import {
	ExportPackage,
	UpdatePackageName,
} from '@state/matter/matter-packages/matter-packages.actions';
import { BusinessState } from '@state/business';
import { ActingForType, MatterFileType } from '@models/matter';
import { MatterMortgagesState } from '@state/matter/matter_mortgages/matter-mortgages.state';
import { DeletePackageConfirmationModalComponent } from 'src/app/private/sale/documents-v2/matter-packages/delete-package-confirmation-modal/delete-packge-confirmation-modal.component';
import { RecipientFormValue } from './envelope.types';
import { ParticipantValidationError } from '../../api-repos/signit/participant-error-formatter';

@Injectable({ providedIn: 'root' })
export class EnvelopeService {
	private toastService = inject(ToastService);
	private modalService = inject(ModalService);
	private drawerService = inject(DrawerService);
	private businessClientState = inject(ClientsState);
	private businessUsersState = inject(BusinessUsersState);
	private signItApiService = inject(SignItApiService);
	private navigationService = inject(NavigationService);
	private router = inject(Router);
	private envelopeConfigRepo = inject(EnvelopeConfigRepo);
	private fb = inject(FormBuilder);

	private UpdatePackageName = dispatch(UpdatePackageName);
	private ExportPackage = dispatch(ExportPackage);
	private downloadFile = dispatch(FileDownloadActions.DownloadFile);

	private loggedInUser = select(AuthState.getLoggedInUser);
	private actingFor_ = select(SelectedMatter.actingFor);
	private matterFileType_ = select(SelectedMatter.matterFileType);
	private allMortgages_ = select(MatterMortgagesState.getAllMortgages);
	private commissioner = select(SelectedMatter.commissioner);
	private isConnectedToSignIt_ = select(BusinessState.isSignITSelected);
	public isLoggedInUserAdmin_ = select(BusinessUsersState.isLoggedInUserAdmin);
	private packageById = select(MatterPackagesSelectors.getPackageById);
	private witness = select(SelectedMatter.witness);

	private eligibleClientSignatories_ = select(
		MatterClients.get_eligibleSignatories,
	);
	private privateLenderSignatories_ = select(
		MatterClients.get_LenderSignatories,
	);
	private envelopeConfigCache_ = signal<
		SigningElementPackageViewModelComplete[]
	>([]);

	private fetching_ = signal(false);
	public syncing_ = this.fetching_.asReadonly();
	public isSignItConnected_ = signal(false);
	public deleteEnvelopeLoading_ = signal(false);

	private commissionerRecipient = computed(() => {
		const commissioner = this.commissioner();
		if (!commissioner?.commissionerUserId) return null;
		return {
			id: commissioner.commissionerUserId,
			name: commissioner.commissionerName,
			participantType: ParticipantTypeEnum.COMMISSIONER,
			email: commissioner.commissionerEmail,
			initials: getInitials(commissioner.commissionerName),
		};
	});
	private witnessRecipient = computed(() => {
		const witness = this.witness();
		if (!witness?.witnessUserId) return null;
		return {
			id: witness.witnessUserId,
			name: witness.witnessName,
			participantType: ParticipantTypeEnum.WITNESS,
			email: witness.witnessEmail,
			initials: getInitials(witness.witnessName),
		};
	});

	private spouseRecipients = toSignal(
		toObservable(
			computed(() =>
				this.eligibleClientSignatories_()
					?.flatMap((client) => {
						return client.associations
							? Object.entries(client.associations)
									.map(([id, assoc]) => (assoc?.types?.spouse ? id : null))
									.filter(Boolean)
							: null;
					})
					.filter(Boolean),
			),
		).pipe(
			switchMap((spouseClientIds) =>
				spouseClientIds.length === 0
					? of([])
					: this.businessClientState.repo.All(
							null,
							where('id', 'in', spouseClientIds),
						),
			),
			map((clients) => clients.filter((client) => clientIsPerson(client))),
		),
		{ initialValue: [] },
	);

	private eligiblePrivateLenders_ = computed(() => {
		const actingFor = this.actingFor_();
		const matterFileType = this.matterFileType_();
		const mortgages = this.allMortgages_();
		const allPrivateLenders = this.privateLenderSignatories_();

		if (matterFileType === MatterFileType.REFINANCE) {
			return actingFor !== ActingForType.MORTGAGOR ? allPrivateLenders : [];
		}

		if (matterFileType === MatterFileType.PURCHASE) {
			const eligibleLenderIds = new Set<string>();

			mortgages
				?.filter((mortgage) => mortgage.actingForMortgagee === true)
				?.forEach((mortgage) => {
					Object.keys(mortgage.lenders || {}).forEach((lenderId) => {
						eligibleLenderIds.add(lenderId);
					});
				});

			return allPrivateLenders.filter((lender) => {
				if (eligibleLenderIds.has(lender.id)) {
					return true;
				}
				return Object.keys(lender.associations || {}).some((associationId) =>
					eligibleLenderIds.has(associationId),
				);
			});
		}

		return [];
	});

	private clientRecipients_ = computed(() => {
		const allClients = this.spouseRecipients()
			.concat(this.eligibleClientSignatories_())
			.concat(this.eligiblePrivateLenders_());

		const clientsMap = new Map<string, ESigningRecipient>();

		allClients?.forEach((client) => {
			if (clientIsPerson(client)) {
				const fullName = client.firstName + ' ' + client.lastName;
				const recipient: ESigningRecipient = {
					id: client.id,
					name: fullName,
					participantType: ParticipantTypeEnum.CLIENT,
					email: client.email,
					initials: getInitials(fullName),
				};

				if (!clientsMap.has(client.id)) {
					clientsMap.set(client.id, recipient);
				}
			}
		});

		return Array.from(clientsMap.values());
	});

	private recipients_ = computed(() => {
		const recipients = this.clientRecipients_();
		const commissionerRecipient = this.commissionerRecipient();
		const witnessRecipient = this.witnessRecipient();

		return recipients
			.concat(
				commissionerRecipient?.id !== witnessRecipient?.id
					? [commissionerRecipient, witnessRecipient]
					: [commissionerRecipient],
			)
			.filter(Boolean);
	});
	public envelopeConfigs_ = computed<SigningElementPackageViewModelComplete[]>(
		() => {
			return this.envelopeConfigCache_().map((envelope) => ({
				...envelope,
				recipients: this.recipients_()
					.map((recipient) => ({
						...recipient,
						hasSigned: envelope.recipients.some(
							(r) => r.id === recipient.id && r.hasSigned,
						),
						isParticipating: envelope.recipients.some(
							(r) => r.id === recipient.id,
						),
						personalSigningUrl: envelope.recipients.find(
							(r) => r.id === recipient.id,
						)?.personalSigningUrl,
					}))
					.sort((a, b) =>
						a.isParticipating === b.isParticipating
							? 0
							: a.isParticipating
								? -1
								: 1,
					),
			}));
		},
	);

	public getParticipatingClientsByEnvelopeId(
		envelopeId: string,
	): Signal<ClientSigningDetailsViewModelComplete[]> {
		return computed(
			() =>
				this.getEnvelopeClients(envelopeId)()?.filter(
					(r) => r.isParticipating,
				) || [],
		);
	}

	public getSignersCount$(form: FormGroup): Observable<string> {
		this.updateEmailValidators(form);

		return form.valueChanges.pipe(
			tap(() => this.updateEmailValidators(form)),
			startWith(form.getRawValue() as Record<string, RecipientFormValue>),
			map((values: Record<string, RecipientFormValue>) => {
				const count = Object.values(values).reduce(
					(acc, recipient) => (recipient.isParticipating ? acc + 1 : acc),
					0,
				);
				return `Signers (${count})`;
			}),
		);
	}

	public async updateEnvelope(
		matterId: string,
		envelopeId: string,
		request: UpdateEnvelopeRequest,
	) {
		const envelope = this.envelopeConfigs_().find(
			(env) => env.id === envelopeId,
		);
		if (!envelope) {
			throw new Error(`Envelope with ID ${envelopeId} not found.`);
		}

		const originalStatus = envelope.status;
		try {
			this.updateExistingEnvelope(envelopeId, () => ({
				status: STATUSES.LOADING,
			}));

			await firstValueFrom(
				this.signItApiService.updateEnvelope(matterId, envelopeId, request),
			);

			this.toastService.success(
				'<strong>Package successfully updated.</strong>',
				2000,
			);
		} catch (error) {
			if (error instanceof HttpErrorResponse && error.status === 400) {
				const errorMessage: string = error.error?.message;
				if (errorMessage) {
					Logger.error(
						`EnvelopeService: Validation error updating envelope with ID: ${envelopeId}: ${error.error.message}`,
					);
					this.toastService.error(errorMessage);
				}
			} else {
				// Fallback for network/unexpected errors
				Logger.error(
					`EnvelopeService: Error updating envelope with ID: ${envelopeId}: ${error}`,
				);
				this.toastService.error('Failed to update envelope');
			}

			throw error;
		} finally {
			this.updateExistingEnvelope(envelopeId, () => ({
				status: originalStatus,
			}));
		}
	}

	public async updateRecipientParticipation(
		envelopeId: string,
		updates: {
			clientId: string;
			isParticipating: boolean;
			participantType: ParticipantTypeEnum;
		}[],
	) {
		const envelopeConfig = await firstValueFrom(
			this.envelopeConfigRepo.Get$(envelopeId),
		);

		await this.envelopeConfigRepo.CreateOrMerge(envelopeId, {
			participantSignStatus: {
				...updates.reduce(
					(acc, { clientId, isParticipating, participantType }) => ({
						...acc,
						[clientId]: !envelopeConfig.participantSignStatus?.[clientId]
							? {
									hasSigned: false,
									clientId,
									participantType,
									isParticipating,
									signingElements: [],
								}
							: {
									isParticipating,
								},
					}),
					{},
				),
			},
		});

		await this.getEnvelopeViewModel(
			this.envelopeConfigRepo.matterId_(),
			envelopeId,
		);
	}

	public getOriginalPackageName(packageId: string) {
		return this.packageById()(packageId)?.packageName ?? '';
	}

	public getRecipientForm(envelopeId?: string) {
		const recipients: ClientSigningDetailsViewModelComplete[] = envelopeId
			? this.getEnvelopeClients(envelopeId)()
			: this.recipients_().map((r) => ({
					...r,
					isParticipating: true,
					hasSigned: false,
				}));

		return {
			form: this.fb.group<{
				[recipientId: string]: ClientSigningDetailsViewModelComplete;
			}>(
				recipients.reduce(
					(acc, recipient) => ({
						...acc,
						[recipient.id]: this.fb.group({
							email: this.fb.control(
								recipient.email,
								recipient.isParticipating
									? [Validators.email, Validators.required]
									: [],
							),
							isParticipating: this.fb.control(recipient.isParticipating),
							participantType: this.fb.control(recipient.participantType),
						}),
					}),
					{},
				),
				{ validators: this.validateMultipleEmailsValidator },
			),
			recipients,
		};
	}

	public async saveRecipientsEmail(
		emails: {
			recipientId: string;
			email: string;
			participantType: ParticipantTypeEnum;
		}[],
	) {
		const businessClientRepo = this.businessClientState.repo;
		const batch = businessClientRepo.Batch();
		const businessUserRepo = this.businessUsersState.repo;
		emails.forEach(({ recipientId, participantType, email }) => {
			if (participantType === ParticipantTypeEnum.CLIENT) {
				batch.set(
					businessClientRepo.DocRef(recipientId),
					{ email },
					{ merge: true },
				);
			} else {
				batch.set(
					businessUserRepo.DocRef(recipientId),
					{ email },
					{ merge: true },
				);
			}
		});
		await batch.commit();
	}

	public async sendEnvelope(
		matterId: string,
		envelopeId: string,
		request: SendEnvelopeRequest,
	) {
		const envelope = this.envelopeConfigs_().find(
			(env) => env.id === envelopeId,
		);
		const participatingClients = envelope.recipients.filter(
			(r) => r.isParticipating,
		);

		if (
			participatingClients?.some((recipient) => !recipient.email) ||
			!this.validateMultipleEmails(participatingClients.map((r) => r.email))
		) {
			this.openEditRecipientModal(envelopeId, true, () =>
				this.openSendEnvelopeModal(matterId, envelopeId),
			);
			return;
		}

		const missingRecipientBlocks = !participatingClients.every((recipient) =>
			envelope.signedElements?.some((block) => block.clientId === recipient.id),
		);

		if (missingRecipientBlocks) {
			const missingSignersNames = participatingClients
				.filter(
					(recipient) =>
						!envelope.signedElements.some(
							(block) => block.clientId === recipient.id,
						),
				)
				.map((recipient) => recipient.name);

			Logger.warn(
				`EnvelopeService: Missing recipient blocks for envelope ID: ${envelopeId}`,
			);

			this.toastService.error(
				`Unable to send due to missing signing field for <strong>${missingSignersNames.join(', ')}</strong>. Please update to send.`,
				5000,
			);

			throw new Error(
				'Missing recipient blocks for some recipients in the envelope',
			);
		}

		try {
			this.updateExistingEnvelope(envelopeId, () => ({
				status: STATUSES.LOADING,
			}));

			// Snapshot the package before sending
			const participatingClientIds = participatingClients.map((client) => ({
				clientId: client.id,
				participantType: client.participantType,
			}));

			await this.snapshotPackageForSigning(
				matterId,
				envelopeId,
				participatingClientIds,
			);

			const results = await firstValueFrom(
				this.signItApiService.sendEnvelope(matterId, envelopeId, request),
			);

			Logger.info(
				`EnvelopeService: Sending envelope with ID: ${envelopeId} and note: ${request.note}`,
			);

			this.updateExistingEnvelope(envelopeId, (envelope) => ({
				status: STATUSES.SENT_ALT,
				sentByName: this.loggedInUser()?.name,
				sentOn: new Date().toISOString(),
				note: request.note ?? '',
				recipients: envelope.recipients.map((recipient) => {
					const personalSigningUrl =
						results.data.envelopeDetails[0].parties?.find(
							(party) => party.email === recipient.email,
						)?.signUrl;

					return {
						...recipient,
						personalSigningUrl,
					};
				}),
			}));
		} catch (error) {
			// Determine error message to display
			let errorMessage = 'Failed to send envelope';

			if (error instanceof HttpErrorResponse && error.status === 400) {
				// Check for participant validation errors first
				const participantErrors: ParticipantValidationError[] | undefined =
					error.error?.participantErrors;

				if (participantErrors && participantErrors.length > 0) {
					// Format participant errors as simple text
					const errorList = participantErrors
						.map((err) => {
							const label =
								err.participantName ||
								err.participantEmail ||
								err.participantType ||
								'Unknown Participant';
							return `${label}: ${err.errorMessage}`;
						})
						.join('; ');

					errorMessage = `Participant validation error${participantErrors.length > 1 ? 's' : ''}: ${errorList}`;
					Logger.error(
						`EnvelopeService: Participant validation errors sending envelope: ${JSON.stringify(participantErrors)}`,
					);
					this.toastService.error(errorMessage, 8000);
				}
				// Check for other 400 errors with a message property
				else if (error.error?.message) {
					errorMessage = error.error.message;
					Logger.error(
						`EnvelopeService: Validation error sending envelope with ID: ${envelopeId}: ${error.error.message}`,
					);
					this.toastService.error(errorMessage);
				}
			} else {
				// Fallback for network/unexpected errors
				Logger.error(
					`EnvelopeService: Error sending envelope with ID: ${envelopeId}: ${error}`,
				);
				this.toastService.error(errorMessage);
			}

			// Revert envelope to DRAFT status and re-throw
			this.updateExistingEnvelope(envelopeId, () => ({
				status: STATUSES.DRAFT,
			}));
			throw error;
		}
	}

	public async getEnvelopeViewModel(matterId: string, envelopeId: string) {
		return await firstValueFrom(
			this.signItApiService.getPackageForDisplay(matterId, envelopeId).pipe(
				map((envelope) => {
					if (!envelope) {
						throw new Error(`Envelope not found for ID: ${envelopeId}`);
					}

					return {
						...envelope,
						status: this.transformStatus(envelope.envelopeStatus),
						recipients: envelope.recipients.map((recipient) => ({
							...recipient,
							isParticipating: true,
						})),
					};
				}),
				tap((envelope) =>
					this.envelopeConfigCache_.update((envelopes) => {
						const existingIndex = envelopes.some(
							(env) => env.id === envelope.id,
						);

						return existingIndex
							? envelopes.map((env) =>
									env.id === envelope.id ? envelope : env,
								)
							: envelopes.concat(envelope);
					}),
				),
				catchError((error) => {
					Logger.error(
						`EnvelopeService: Error fetching package for display: ${error}`,
					);
					return throwError(() => error);
				}),
			),
		);
	}

	public async getEnvelopeViewModels(matterId: string) {
		this.fetching_.set(true);

		return await firstValueFrom(
			this.signItApiService.getAllPackagesForDisplay(matterId).pipe(
				switchMap((envelopes) => {
					if (!envelopes || !Array.isArray(envelopes)) {
						this.fetching_.set(false);
						return of([]);
					}

					if (!this.isConnectedToSignIt_()) {
						const draftDeletions = envelopes
							.filter(
								(env) =>
									env?.id && env.envelopeStatus === EnvelopeStatusEnum.DRAFT,
							)
							.map((env) =>
								this.signItApiService.deleteEnvelope(matterId, env.id).pipe(
									tap(() => {
										this.envelopeConfigCache_.update((cached) =>
											cached.filter((e) => e.id !== env.id),
										);
									}),
									catchError((err) => {
										Logger.exception(err, {
											message: 'Failed to delete envelope',
											matterId,
											envelopeId: env.id,
										});
										return of(null);
									}),
								),
							);

						if (draftDeletions.length) {
							this.deleteEnvelopeLoading_.set(true);
							return forkJoin(draftDeletions).pipe(
								tap(() => this.deleteEnvelopeLoading_.set(false)),
								map(() => envelopes),
							);
						}
					}

					return of(envelopes);
				}),
				map((envelopes) =>
					envelopes.map((envelope) => ({
						...envelope,
						status: this.transformStatus(envelope?.envelopeStatus),
						envelopeName: 'Envelope Name',
						recipients: envelope.recipients.map((recipient) => ({
							...recipient,
							isParticipating: true,
						})),
					})),
				),
				tap((envelopes) => {
					this.envelopeConfigCache_.set(envelopes);
					this.fetching_.set(false);
				}),
				catchError((error) => {
					this.fetching_.set(false);
					Logger.exception(error, {
						message: 'Failed to fetch envelopes for matter',
						matterId,
					});
					return throwError(() => new Error('Failed to fetch envelopes'));
				}),
			),
		);
	}

	public async preparePackageForSigning(
		matterId: string,
		packageId: string,
		participatingClientIds: PreparePackageForSigningOptions['participatingClientIds'],
	) {
		Logger.info(
			`EnvelopeService: Preparing package for signing with ID: ${packageId}`,
		);
		try {
			await firstValueFrom(
				this.signItApiService.preparePackageForSigning(
					matterId,
					packageId,
					participatingClientIds,
				),
			);

			// Close modal before navigating
			this.modalService.closeModal();

			// Navigate with skipSnapshot param since we just snapshotted
			await this.router.navigate(
				['/app/matter', matterId, 'documents', 'envelopes', packageId],
				{ queryParams: { skipSnapshot: 'true' } },
			);
		} catch (error) {
			// Determine error message to display
			let errorMessage = 'Failed to prepare package for signing';

			if (error instanceof HttpErrorResponse && error.status === 400) {
				// Check for participant validation errors first
				const participantErrors: ParticipantValidationError[] | undefined =
					error.error?.participantErrors;

				if (participantErrors && participantErrors.length > 0) {
					// Format participant errors as simple text
					const errorList = participantErrors
						.map((err) => {
							const label =
								err.participantName ||
								err.participantEmail ||
								err.participantType ||
								'Unknown Participant';
							return `${label}: ${err.errorMessage}`;
						})
						.join('; ');

					errorMessage = `Participant validation error${participantErrors.length > 1 ? 's' : ''}: ${errorList}`;
					Logger.error(
						`EnvelopeService: Participant validation errors preparing package: ${JSON.stringify(participantErrors)}`,
					);
					this.toastService.error(errorMessage, 8000);
				}
				// Check for other 400 errors with a message property
				else if (error.error?.message) {
					errorMessage = error.error.message;
					Logger.error(
						`EnvelopeService: Validation error preparing package: ${error.error.message}`,
					);
					this.toastService.error(errorMessage);
				}
			} else {
				// Fallback for network/unexpected errors
				Logger.error(
					`EnvelopeService: Error preparing package for signing: ${error}`,
				);
				this.toastService.error(errorMessage);
			}
			throw error;
		}
	}

	public async snapshotPackageForSigning(
		matterId: string,
		packageId: string,
		participatingClientIds: PreparePackageForSigningOptions['participatingClientIds'],
	): Promise<boolean> {
		Logger.info(
			`EnvelopeService: Snapshotting package for signing with ID: ${packageId}`,
		);
		try {
			await firstValueFrom(
				this.signItApiService.preparePackageForSigning(
					matterId,
					packageId,
					participatingClientIds,
					false,
				),
			);
			return true;
		} catch (error) {
			Logger.error(
				`EnvelopeService: Error snapshotting package for signing: ${error}`,
			);
			return false;
		}
	}

	public async downloadEnvelope(envelopeId: string) {
		Logger.info(`EnvelopeService: Downloading envelope with ID: ${envelopeId}`);
		const envelope = this.envelopeConfigs_().find(
			(env) => env.id === envelopeId,
		);

		if (!envelope) {
			Logger.error(`EnvelopeService: Envelope with ID ${envelopeId} not found`);
			this.toastService.error('Envelope not found');
			return;
		}

		if (envelope.status === STATUSES.DRAFT) {
			return this.ExportPackage(envelopeId);
		}

		const matterId = this.envelopeConfigRepo.matterId_();

		if (envelope.status === STATUSES.COMPLETE) {
			try {
				const response = await firstValueFrom(
					this.signItApiService.getCompletedEnvelopeDocument(
						matterId,
						envelopeId,
					),
				);

				if (response && response.base64Pdf) {
					const fileName =
						response.fileName ||
						`${envelope.packageName || 'envelope'}-signed.pdf`;
					downloadBase64File('application/pdf', response.base64Pdf, fileName);
					return;
				}
			} catch (error) {
				Logger.error(
					`EnvelopeService: Error downloading completed envelope ${envelopeId}: ${error}`,
				);
				// Fall through to regular download if API fails
			}
		}

		// For draft envelopes or if API fails, use direct URL download
		const pdfUrl =
			envelope.status === STATUSES.COMPLETE
				? envelope.completedMergedPdfUrl
				: envelope.mergedPdfUrl;

		if (!pdfUrl) {
			Logger.error(
				`EnvelopeService: No PDF URL found for envelope ID ${envelopeId}`,
			);
			this.toastService.error('No PDF available for download');
			return;
		}

		// Extract filename from GCP path, fallback to correct naming convention
		let fileName: string;
		try {
			const urlObj = new URL(pdfUrl, window.location.origin);
			const pathname = urlObj.pathname;
			const lastSegment = pathname.substring(pathname.lastIndexOf('/') + 1);
			fileName =
				decodeURIComponent(lastSegment) ||
				`${envelope.packageName || 'envelope'}-signed.pdf`;
		} catch {
			fileName = `${envelope.packageName || 'envelope'}-signed.pdf`;
		}

		await downloadEnvelopePdf(pdfUrl, fileName, this.downloadFile);
	}

	public async renameEnvelope(envelopeId: string, newName: string) {
		try {
			await this.envelopeConfigRepo.CreateOrMerge(envelopeId, {
				packageName: newName,
			});

			this.UpdatePackageName({ id: envelopeId, packageName: newName });
			this.envelopeConfigCache_.update((envelopes) =>
				envelopes.map((env) =>
					env.id === envelopeId ? { ...env, packageName: newName } : env,
				),
			);
		} catch (error) {
			Logger.error(
				`EnvelopeService: Error updating envelope name for envelope ${envelopeId}: ${error}`,
			);
			this.toastService.error('Failed to rename envelope');
			throw error;
		}
	}

	public async updateNotes(envelopeId: string, note: string) {
		try {
			await this.envelopeConfigRepo.CreateOrMerge(envelopeId, {
				note,
			});

			this.envelopeConfigCache_.update((envelopes) =>
				envelopes.map((env) =>
					env.id === envelopeId ? { ...env, note } : env,
				),
			);
		} catch (error) {
			Logger.error(
				`EnvelopeService: Error updating package notes for package ${envelopeId}: ${error}`,
			);
			this.toastService.error('Failed to update package notes');
			throw error;
		}
	}

	public async setIsSignatureDrawOnly(envelopeId: string, isDrawOnly: boolean) {
		try {
			await this.envelopeConfigRepo.CreateOrMerge(envelopeId, {
				isSignatureDrawOnly: isDrawOnly,
			});
			this.envelopeConfigCache_.update((envelopes) =>
				envelopes.map((env) =>
					env.id === envelopeId
						? { ...env, isSignatureDrawOnly: isDrawOnly }
						: env,
				),
			);
		} catch (error) {
			Logger.error(
				`EnvelopeService: Error updating isSignatureDrawOnly for envelope ${envelopeId}: ${error}`,
			);
			this.toastService.error('Failed to update signature type');
			throw error;
		}
	}

	public openSendEnvelopeModal(matterId: string, envelopeId: string) {
		this.modalService.openModal(ESigningSendEnvelopeModalComponent, {
			data: { matterId, envelopeId },
			title: 'Send Package',
			size: 'md-sm',
			secondaryAction: {
				text: 'Cancel',
				action: () => {
					this.modalService.closeModal();
				},
			},
		});
	}

	public openEditRecipientModal(
		envelopeId: string,
		submitted = false,
		onClose?: () => void,
	) {
		this.modalService.openModal(ESigningEditRecipientModalViewComponent, {
			data: {
				submitted,
				onPrimaryActionSuccess: onClose,
				envelopeId,
			},
			title: 'Edit Signers',
			size: 'md-sm',
			secondaryAction: {
				text: 'Back',
				action: () => (onClose ? onClose() : this.modalService.closeModal()),
			},
		});
	}

	public openPopConfirmForDelete(matterId: string, envelopeId: string) {
		this.modalService.openModal(DeletePackageConfirmationModalComponent, {
			title: 'Delete E-Signing Package',
			size: 'sm',
			type: UI_TYPES.DANGER,
			data: {
				subTitle: 'Are you sure you want to delete this e-signing package?',
				message:
					'Deleting this e-signing package will revert the package to a standard one, and all related e-signing details will be removed from your matter. This action cannot be undone.',
			},
			primaryAction: {
				text: 'Delete Package',
				action: async () => {
					Logger.info(
						`EnvelopeService: Deleting envelope with ID: ${envelopeId}`,
					);

					try {
						this.modalService.closeModal();
						await firstValueFrom(
							this.signItApiService.deleteEnvelope(matterId, envelopeId),
						);
						this.goToEnvelopesView(matterId);
						this.envelopeConfigCache_.update((envelopes) =>
							envelopes.filter((env) => env.id !== envelopeId),
						);
					} catch (error) {
						Logger.error(
							`EnvelopeService: Error deleting envelope with ID: ${envelopeId}: ${error}`,
						);
						this.openPopConfirmForDelete(matterId, envelopeId);
						throw error;
					}
				},
			},
			secondaryAction: {
				text: 'Cancel',
				action: () => this.modalService.closeModal(),
			},
		});
	}

	public openPopConfirmForCancel(matterId: string, envelopeId: string) {
		this.openPopConfirm({
			title: 'Cancel Package',
			subTitle: 'Are you sure you want to cancel this package?',
			message: `This action cannot be undone. Cancelling will immediately stop the signing process for all recipients,
				 invalidate any collected signatures, and return the package to your Drafts for editing. You won't be
				 charged for this.`,
			primaryAction: {
				text: 'Cancel Package',
				action: async () => {
					Logger.info(
						`EnvelopeService: Cancelling envelope with ID: ${envelopeId}`,
					);
					const envelopeStatusRevertValue = this.envelopeConfigCache_().find(
						(env) => env.id === envelopeId,
					)?.status;
					try {
						this.modalService.closeModal();
						this.updateExistingEnvelope(envelopeId, () => ({
							status: STATUSES.DRAFT,
						}));

						this.toastService.success(
							'<strong>Package successfully canceled.</strong> The package has been moved to Draft status.',
							2000,
						);

						if (!this.isConnectedToSignIt_()) {
							await this.convertESigningToStandardPackage(envelopeId, matterId);
						} else {
							await firstValueFrom(
								this.signItApiService.cancelEnvelope(matterId, envelopeId),
							);
						}
					} catch (error) {
						Logger.error(
							`EnvelopeService: Error cancelling envelope with ID: ${envelopeId}: ${error}`,
						);
						this.updateExistingEnvelope(envelopeId, () => ({
							status: envelopeStatusRevertValue,
						}));
						this.openPopConfirmForCancel(matterId, envelopeId);
						throw error;
					}
				},
			},
			secondaryActionText: 'Go Back',
		});
	}

	public async convertESigningToStandardPackage(
		envelopeId: string,
		matterId: string,
	) {
		this.updateExistingEnvelope(envelopeId, () => ({
			status: STATUSES.LOADING,
		}));

		await firstValueFrom(
			this.signItApiService.deleteEnvelope(matterId, envelopeId).pipe(
				tap(() => {
					this.envelopeConfigCache_.update((list) =>
						list.filter((env) => env.id !== envelopeId),
					);
					this.goToEnvelopeOrPackageHandler(matterId, envelopeId);
				}),
			),
		);
	}

	public openEnvelopePreviewDrawer(
		envelope: SigningElementPackageViewModelComplete,
		title?: string,
	) {
		const completed = envelope.status === STATUSES.COMPLETE;
		const pdfUrl = completed
			? envelope.completedMergedPdfUrl
			: envelope.mergedPdfUrl;

		this.drawerService.openDrawer(ViewEnvelopeDrawerComponent, {
			title: title || 'Envelope Preview',
			data: {
				blocks: envelope.signedElements,
				pages: envelope.pagesAsImages,
				recipients: envelope.recipients,
				pdfUrl: pdfUrl,
				fileName: envelope.packageName,
			},
		});
	}

	public async persistBlockChange(
		matterId: string,
		envelopeId: string,
		eSigningBlockChange: ESigningBlockChange,
	) {
		try {
			const signingElement = eSigningBlockChange.block;
			switch (eSigningBlockChange.action) {
				case 'ADD': {
					this.envelopeConfigCache_.update((envelopes) =>
						envelopes.map((env) =>
							env.id === envelopeId
								? {
										...env,
										signedElements: [...env.signedElements, signingElement],
									}
								: env,
						),
					);
					await firstValueFrom(
						this.signItApiService.upsertBlock(
							matterId,
							envelopeId,
							eSigningBlockChange.block,
						),
					);
					break;
				}
				case 'UPDATE': {
					this.envelopeConfigCache_.update((envelopes) =>
						envelopes.map((env) =>
							env.id === envelopeId
								? {
										...env,
										signedElements: env.signedElements.map((el) =>
											el.id === signingElement.id ? signingElement : el,
										),
									}
								: env,
						),
					);
					await firstValueFrom(
						this.signItApiService.upsertBlock(
							matterId,
							envelopeId,
							eSigningBlockChange.block,
						),
					);
					break;
				}
				case 'DELETE': {
					this.envelopeConfigCache_.update((envelopes) =>
						envelopes.map((env) =>
							env.id === envelopeId
								? {
										...env,
										signedElements: env.signedElements.filter(
											(el) => el.id !== signingElement.id,
										),
									}
								: env,
						),
					);
					await firstValueFrom(
						this.signItApiService.deleteBlock(
							matterId,
							envelopeId,
							eSigningBlockChange.block.id,
						),
					);
					break;
				}
			}
		} catch (error) {
			Logger.error(
				`EnvelopeService: Error ${eSigningBlockChange.action} block for envelope ${envelopeId}: ${error}`,
			);
			throw error;
		}
	}

	public goToEnvelopeOrPackageHandler(
		matterId: string,
		packageId: string,
	): void {
		const isConnected = this.isConnectedToSignIt_();
		const envelope = this.envelopeConfigs_().find((e) => e.id === packageId);
		const pkg = this.packageById()(packageId);

		// Check if package has been prepared for e-signing (has the signing package flag set)
		const isPreparedForESigning = pkg?.packageTypes?.isSigningPackage === true;

		if (!envelope || !isPreparedForESigning) {
			this.navigationService.goToMatter_Packages_Package(matterId, packageId);
			return;
		}

		if (
			isConnected ||
			(!isConnected && envelope.envelopeStatus !== EnvelopeStatusEnum.DRAFT)
		) {
			this.goToEnvelopeView(matterId, packageId);
		} else {
			this.navigationService.goToMatter_Packages_Package(matterId, packageId);
		}
	}

	public goToEnvelopesView(matterId: string) {
		this.navigationService.goToMatter_Documents_Packages(matterId);
	}

	private updateEmailValidators = (form: FormGroup) => {
		const value = form.getRawValue() as Record<string, RecipientFormValue>;

		Object.keys(value).forEach((key) => {
			const recipient: RecipientFormValue = value[key];
			const emailControl: AbstractControl | null = form.get([key, 'email']);

			if (emailControl) {
				if (recipient.isParticipating) {
					emailControl.setValidators([Validators.required, Validators.email]);
				} else {
					emailControl.clearValidators();
				}
				emailControl.updateValueAndValidity({ emitEvent: false });
			}
		});
	};

	private goToEnvelopeView(matterId: string, packageId: string) {
		this.navigationService.goToMatter_Documents_Envelopes_Envelope(
			matterId,
			packageId,
		);
	}

	private openPopConfirm({
		title,
		subTitle,
		message,
		primaryAction,
		secondaryActionText,
	}: Omit<ComponentConfig, 'secondaryAction'> & {
		secondaryActionText?: string;
	}) {
		this.modalService.openModal({
			title,
			subTitle,
			message,
			primaryAction,
			secondaryAction: {
				text: secondaryActionText || 'Cancel',
				action: () => this.modalService.closeModal(),
			},
			type: UI_TYPES.DANGER,
			size: 'sm',
		});
	}

	private updateExistingEnvelope(
		envelopeId: string,
		envelopeUpdates: (
			envelope: SigningElementPackageViewModelComplete,
		) => Partial<SigningElementPackageViewModelComplete>,
	) {
		this.envelopeConfigCache_.update((envelopes) => {
			return envelopes.map((env) =>
				env.id === envelopeId ? { ...env, ...envelopeUpdates(env) } : env,
			);
		});
	}

	private transformStatus(status: EnvelopeStatusEnum): STATUSES {
		switch (status) {
			case EnvelopeStatusEnum.DRAFT:
			case EnvelopeStatusEnum.CANCELLED:
				return STATUSES.DRAFT;
			case EnvelopeStatusEnum.WAITING:
				return STATUSES.IN_PROGRESS;
			case EnvelopeStatusEnum.PENDING:
			case EnvelopeStatusEnum.SENT:
				return STATUSES.SENT_ALT;
			case EnvelopeStatusEnum.COMPLETED:
				return STATUSES.COMPLETE;
			default:
				return STATUSES.UNKNOWN_STATUS;
		}
	}

	private getEnvelopeClients(
		envelopeId: string,
	): Signal<ClientSigningDetailsViewModelComplete[]> {
		return computed(
			() =>
				this.envelopeConfigs_().find((envelope) => envelope.id === envelopeId)
					.recipients,
		);
	}

	private validateMultipleEmailsValidator = (control: FormGroup) => {
		const emails = Object.values(control.value).map(
			(recipient: ClientSigningDetailsViewModelComplete) => recipient.email,
		);
		if (this.validateMultipleEmails(emails)) {
			return null;
		}
		return { duplicateEmails: true };
	};

	private validateMultipleEmails(emails: string[] = []): boolean {
		const validEmails = emails.filter(Boolean);
		return new Set(validEmails).size === validEmails.length;
	}
}
